import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HomeWidgetType } from '../../../settings/validation/HomeWidget';
import { HomeWidgetsContext } from '../components/home-widgets/HomeWidgetsContextProvider';

export interface WidgetOption {
  id: HomeWidgetType;
  labelKey: string;
  enabled: boolean;
}

export const useHomePageContextMenu = () => {
  const { t } = useTranslation();
  const { widgets, modifyConf, addWidget } = useContext(HomeWidgetsContext);

  const [contextMenuPosition, setContextMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const contextMenuOpen = Boolean(contextMenuPosition);

  const widgetOptions: WidgetOption[] = useMemo(
    () => [
      {
        id: HomeWidgetType.relevantWidget,
        labelKey: 'widget.myCurrentDay',
        enabled: widgets.some((w) => w.type === HomeWidgetType.relevantWidget),
      },
      {
        id: HomeWidgetType.calendarWidget,
        labelKey: 'widget.myAppointments',
        enabled: widgets.some((w) => w.type === HomeWidgetType.calendarWidget),
      },
      {
        id: HomeWidgetType.overviewWidget,
        labelKey: 'widget.overview',
        enabled: widgets.some((w) => w.type === HomeWidgetType.overviewWidget),
      },
    ],
    [widgets]
  );

  const handleContextMenuClose = () => {
    setContextMenuPosition(null);
  };

  const handleContextMenu = (
    event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
  ) => {
    if ((event.target as HTMLElement).closest('.MuiBackdrop-root')) {
      event.preventDefault();
      handleContextMenuClose();
      return;
    }

    // Cancel if the target is a react-grid-item or its child
    if (
      (event.target as HTMLElement).closest('.react-grid-item') ||
      (event.target as HTMLElement).closest('.MuiBox-root')
    ) {
      return;
    }

    event.preventDefault();

    // Extract coordinates from either mouse or touch event
    const getEventCoordinates = (
      e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
    ) => {
      if ('clientX' in e && 'clientY' in e) {
        // Mouse event
        return { x: e.clientX, y: e.clientY };
      } else if ('touches' in e && e.touches.length > 0) {
        // Touch event with active touches
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if ('changedTouches' in e && e.changedTouches.length > 0) {
        // Touch event (like touchend) with changedTouches
        return {
          x: e.changedTouches[0].clientX,
          y: e.changedTouches[0].clientY,
        };
      }
      // Fallback to 0,0 if no coordinates found
      return { x: 0, y: 0 };
    };

    const coordinates = getEventCoordinates(event);
    setContextMenuPosition({
      top: coordinates.y,
      left: coordinates.x,
    });
  };

  const handleWidgetToggle = (widgetType: HomeWidgetType) => {
    const existingWidget = widgets.find((w) => w.type === widgetType);

    if (existingWidget) {
      // Remove widget if it exists
      modifyConf({
        widgets: { remove: [existingWidget.id] },
        layout: { remove: [existingWidget.id] },
      });
    } else {
      // Add widget with default size and placement using addWidget function
      const newWidget = {
        id: widgetType,
        type: widgetType,
        title: t(
          widgetOptions.find((opt) => opt.id === widgetType)?.labelKey || ''
        ),
      };

      addWidget(newWidget, { useDefaultLayouts: true });
    }
  };

  return {
    contextMenuPosition,
    contextMenuOpen,
    widgetOptions,
    handleContextMenu,
    handleContextMenuClose,
    handleWidgetToggle,
  };
};
